import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import StillWaterLegendControl from '@App/components/Map/controls/StillWaterLegendControl/StillWaterLegendControl';
import PanelHeader from '@App/components/PanelHeader/PanelHeader';
import PanelList, {
  getPropertyRecord,
} from '@App/components/PanelList/PanelList';
import {
  useStillWaterCells,
  useStillWaterDetails,
} from '@services/query.service';
import { getFeaturesBounds } from '@utils/geometry.utils';

import {
  getSourceColorExpression,
  layers,
  sources,
} from '../../sources.layers';
import { inactiveSourceLayers } from '../still-water.layers';

import '../still-water.css';

const numberFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});
const stillWaterCellsSourceId = 'active-still-water-cells';
const stillWaterCellsFillLayerId = 'active-still-water-cells-fill';
const stillWaterCellsOutlineLayerId = 'active-still-water-cells-outline';
const selectedStillWaterCellLayerId = 'selected-still-water-cell';

function formatEnergyPerYear(value) {
  if (value === undefined || value === null || value === '') return '-';

  const numericValue = Number(value);
  const absoluteValue = Math.abs(numericValue);
  if (absoluteValue >= 1_000_000) {
    return `${numberFormatter.format(numericValue / 1_000_000)} GWh/jaar`;
  }
  if (absoluteValue >= 1_000) {
    return `${numberFormatter.format(numericValue / 1_000)} MWh/jaar`;
  }

  return `${numberFormatter.format(numericValue)} kWh/jaar`;
}

function formatTemperature(value) {
  if (value === undefined || value === null || value === '') return '-';

  return `${numberFormatter.format(Number(value))} °C`;
}

function getCellProperties(cell) {
  if (cell?.properties && typeof cell.properties === 'object') {
    return cell.properties;
  }

  return cell && typeof cell === 'object' ? cell : {};
}

function getCellList(cells) {
  if (Array.isArray(cells)) return cells;
  if (Array.isArray(cells?.features)) return cells.features;
  if (Array.isArray(cells?.data)) return cells.data;
  if (Array.isArray(cells?.items)) return cells.items;
  if (Array.isArray(cells?.results)) return cells.results;
  return [];
}

function getCellValue(properties, ...keys) {
  const key = keys.find(candidate => properties[candidate] !== undefined);
  return key ? properties[key] : undefined;
}

function normalizeCellProperties(properties) {
  return {
    ...properties,
    gemiddelde_delta_bronsleutel6: getCellValue(
      properties,
      'gemiddelde_delta_bronsleutel6',
      'DeltaTavg_bronsleutel6',
    ),
  };
}

function getCellMaximums(cells) {
  const maximums = {
    'potential': 0,
    'temperature': 0,
    'heat-delivered': 0,
  };
  const cellList = getCellList(cells);

  cellList.forEach((cell) => {
    const properties = getCellProperties(cell);
    const potential = Number(getCellValue(
      properties,
      'Bronpotentieel_jaar_bronsleutel6',
    )) / 1_000_000;
    const temperature = Math.abs(Number(getCellValue(
      properties,
      'gemiddelde_delta_bronsleutel6',
      'DeltaTavg_bronsleutel6',
    )));
    const heatDelivered = Number(
      getCellValue(properties, 'totale_warmte_geleverd_combosleutel_bs6_vs1'),
    ) / 1_000_000;
    if (Number.isFinite(potential)) maximums.potential = Math.max(maximums.potential, potential);
    if (Number.isFinite(temperature)) {
      maximums.temperature = Math.max(maximums.temperature, temperature);
    }
    if (Number.isFinite(heatDelivered)) {
      maximums['heat-delivered'] = Math.max(maximums['heat-delivered'], heatDelivered);
    }
  });

  if (maximums.potential <= 0) maximums.potential = 1;
  if (maximums.temperature <= 0) maximums.temperature = 1;
  if (maximums['heat-delivered'] <= 0) maximums['heat-delivered'] = 1;

  return maximums;
}

function StillWaterCell() {
  const { map } = useOutletContext();
  const { stilstaandWaterId, stilstaandWaterCellId } = useParams();
  const navigate = useNavigate();
  const legendMode = useSelector(state => state.sourceLegend.selectedMode);
  const { data } = useStillWaterDetails(stilstaandWaterId);
  const { data: cells } = useStillWaterCells(stilstaandWaterId);
  const cellMaximums = useMemo(() => getCellMaximums(cells), [cells]);
  const cellList = Array.isArray(cells) ? cells : cells?.features || [];
  const selectedCellById = cellList.find((cell) => {
    const properties = getCellProperties(cell);
    return String(
      cell.id
      ?? properties.ID_cel
      ?? properties.ID_stilstaand_cel,
    ) === String(stilstaandWaterCellId);
  });
  const selectedCell = selectedCellById
    || cellList[Number(stilstaandWaterCellId)];
  const selectedCellProperties = getCellProperties(selectedCell);
  const selectedCellId = selectedCell
    ? (
        selectedCellProperties.ID_cel
        ?? selectedCellProperties.ID_stilstaand_cel
        ?? selectedCell.id
      )
    : undefined;
  const record = getPropertyRecord(data);
  const cellRecord = getPropertyRecord(selectedCell);
  const rawName = record.naam || record.Naam || record.name;
  const name = rawName && String(rawName).toLowerCase() !== 'none'
    ? rawName
    : `STILSTAAND WATER ${stilstaandWaterId}`;
  const cellId = cellRecord.ID_cel ?? stilstaandWaterCellId;
  const details = [
    [
      'Totale bronpotentieel',
      formatEnergyPerYear(cellRecord.Bronpotentieel_jaar_bronsleutel6),
    ],
    [
      'Resterend bronpotentieel',
      formatEnergyPerYear(
        cellRecord.Rest_bronpotentieel_jaar_bronsleutel6
        ?? cellRecord.Rest_bronpotentieel_jaar_sleutel6,
      ),
    ],
    [
      'Gemiddelde delta T',
      formatTemperature(cellRecord.DeltaTavg_bronsleutel6),
    ],
    [
      'Minimaal piekpotentieel',
      formatEnergyPerYear(
        cellRecord.Bronpotentieel_minpiek_bronlsleutel6
        ?? cellRecord.Bronpotentieel_minpiek_bronsleutel6,
      ),
    ],
    [
      'Maximaal piekpotentieel',
      formatEnergyPerYear(
        cellRecord.Bronpotentiel_maxpiek_bronsleutel6
        ?? cellRecord.Bronpotentieel_maxpiek_bronsleutel6,
      ),
    ],
    [
      'Warmte geleverd',
      formatEnergyPerYear(cellRecord.totale_warmte_geleverd_combosleutel_bs6_vs1),
    ],
  ];

  useEffect(() => {
    if (!map) return undefined;

    const legendControl = new StillWaterLegendControl(cellMaximums);
    map.addControl(legendControl, 'bottom-right');

    return () => {
      map.removeControl(legendControl);
    };
  }, [cellMaximums, map]);

  useEffect(() => {
    if (!map || !cells) return undefined;

    const cellList = getCellList(cells);
    const cellFeatures = cellList.map((cell, index) => {
      const properties = normalizeCellProperties(
        cell.type === 'Feature'
          ? getCellProperties(cell)
          : Object.fromEntries(
              Object.entries(cell).filter(([key]) => key !== 'geometry'),
            ),
      );
      return cell.type === 'Feature'
        ? {
            ...cell,
            id: cell.id ?? properties.ID_cel ?? properties.ID_stilstaand_cel ?? index,
            properties,
          }
        : {
            type: 'Feature',
            id: properties.ID_cel ?? properties.ID_stilstaand_cel ?? index,
            geometry: cell.geometry,
            properties,
          };
    });
    const featureCollection = {
      type: 'FeatureCollection',
      features: cellFeatures,
    };

    if (map.getLayer(stillWaterCellsOutlineLayerId)) {
      map.removeLayer(stillWaterCellsOutlineLayerId);
    }
    if (map.getLayer(stillWaterCellsFillLayerId)) {
      map.removeLayer(stillWaterCellsFillLayerId);
    }
    if (map.getSource(stillWaterCellsSourceId)) {
      map.removeSource(stillWaterCellsSourceId);
    }

    map.addSource(stillWaterCellsSourceId, {
      type: 'geojson',
      data: featureCollection,
    });
    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;
    map.addLayer({
      id: stillWaterCellsFillLayerId,
      type: 'fill',
      source: stillWaterCellsSourceId,
      paint: {
        'fill-color': getSourceColorExpression(
          legendMode,
          'stillWater',
          false,
          cellMaximums[legendMode],
        ),
        'fill-opacity': 0.6,
      },
    }, layerAfterRoadLabelsId);
    map.addLayer({
      id: stillWaterCellsOutlineLayerId,
      type: 'line',
      source: stillWaterCellsSourceId,
      minzoom: 13,
      paint: {
        'line-color': [
          'case',
          ['==', ['id'], selectedCellId],
          '#217F9A',
          '#fff',
        ],
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          13,
          [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            2,
            0.5,
          ],
          16,
          [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            4,
            1.5,
          ],
        ],
      },
    }, layerAfterRoadLabelsId);
    map.setFilter(stillWaterCellsOutlineLayerId, ['!=', ['id'], selectedCellId]);
    map.addLayer({
      id: selectedStillWaterCellLayerId,
      type: 'line',
      source: stillWaterCellsSourceId,
      minzoom: 13,
      filter: ['==', ['id'], selectedCellId],
      paint: {
        'line-color': '#217F9A',
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          13,
          2,
          16,
          4,
        ],
      },
    });
    let hoveredCellId;
    const handleCellMouseMove = (event) => {
      const feature = event.features?.[0];
      if (feature?.id === undefined || feature?.id === null) return;

      if (hoveredCellId !== undefined && hoveredCellId !== feature.id) {
        map.setFeatureState(
          { source: stillWaterCellsSourceId, id: hoveredCellId },
          { hover: false },
        );
      }
      hoveredCellId = feature.id;
      map.setFeatureState(
        { source: stillWaterCellsSourceId, id: hoveredCellId },
        { hover: true },
      );
      map.getCanvas().style.cursor = 'pointer';
    };
    const handleCellMouseLeave = () => {
      if (hoveredCellId !== undefined) {
        map.setFeatureState(
          { source: stillWaterCellsSourceId, id: hoveredCellId },
          { hover: false },
        );
        hoveredCellId = undefined;
      }
      map.getCanvas().style.cursor = '';
    };
    map.on('mousemove', stillWaterCellsFillLayerId, handleCellMouseMove);
    map.on('mouseleave', stillWaterCellsFillLayerId, handleCellMouseLeave);
    const handleCellClick = (event) => {
      const cellId = event.features?.[0]?.id;
      if (cellId !== undefined && cellId !== null) {
        navigate(`/bronnen/stilstaand-water/${stilstaandWaterId}/${cellId}`);
      }
    };
    map.on('click', stillWaterCellsFillLayerId, handleCellClick);

    return () => {
      map.off('mousemove', stillWaterCellsFillLayerId, handleCellMouseMove);
      map.off('mouseleave', stillWaterCellsFillLayerId, handleCellMouseLeave);
      map.off('click', stillWaterCellsFillLayerId, handleCellClick);
      handleCellMouseLeave();
      if (map.getLayer(selectedStillWaterCellLayerId)) {
        map.removeLayer(selectedStillWaterCellLayerId);
      }
      if (map.getLayer(stillWaterCellsOutlineLayerId)) {
        map.removeLayer(stillWaterCellsOutlineLayerId);
      }
      if (map.getLayer(stillWaterCellsFillLayerId)) {
        map.removeLayer(stillWaterCellsFillLayerId);
      }
      if (map.getSource(stillWaterCellsSourceId)) {
        map.removeSource(stillWaterCellsSourceId);
      }
    };
  }, [
    cellMaximums,
    cells,
    legendMode,
    map,
    navigate,
    selectedCellId,
    stilstaandWaterId,
  ]);

  useEffect(() => {
    if (!map || !data?.geometry) return undefined;

    const featureBounds = getFeaturesBounds([selectedCell || data]);
    if (!featureBounds) return undefined;

    const firstFrame = requestAnimationFrame(() => {
      map.resize();
      map.fitBounds(featureBounds, {
        padding: {
          top: 48,
          right: 48,
          bottom: 48,
          left: 48,
        },
        maxZoom: 16,
        duration: 1000,
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
    };
  }, [data, map, selectedCell]);

  useEffect(() => {
    if (!map) return undefined;

    const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;
    if (!s3BucketUrl) return undefined;

    const sourceEntries = Object.values(sources);
    const layerEntries = Object.values(layers);
    sourceEntries.forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, {
          type: source.type,
          url: `pmtiles://${s3BucketUrl}/${source.pmtilesFile}`,
          promoteId: source.promoteId,
        });
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;
    layerEntries.forEach((layer) => {
      if (!map.getLayer(layer.id)) map.addLayer(layer, layerAfterRoadLabelsId);
    });

    const activeStillWaterId = Number(stilstaandWaterId);
    const inactiveStillWaterFilter = [
      '!=',
      ['get', 'ID_stilstaand'],
      activeStillWaterId,
    ];
    map.setFilter(layers.stillWater.id, inactiveStillWaterFilter);
    map.setFilter(layers.stillWaterOutline.id, inactiveStillWaterFilter);

    Object.entries(inactiveSourceLayers).forEach(([layerName, paintProperties]) => {
      Object.entries(paintProperties).forEach(([property, value]) => {
        map.setPaintProperty(layers[layerName].id, property, value);
      });
    });

    const navigationTargets = {
      [layers.flowingWaterHitArea.id]: feature => `/bronnen/stromend-water/${feature.id}`,
      [layers.stillWater.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.stillWaterOutline.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.rwzi.id]: feature => `/bronnen/rwzi/${feature.id}`,
    };
    const clickHandlers = Object.entries(navigationTargets).map(([layerId, getPath]) => {
      const handler = (event) => {
        const feature = event.features?.[0];
        if (feature?.id !== undefined && feature?.id !== null) {
          navigate(getPath(feature));
        }
      };
      map.on('click', layerId, handler);
      return { layerId, handler };
    });

    const interactiveLayerIds = Object.keys(navigationTargets);
    let hoveredFeature;
    const clearHoveredFeature = () => {
      if (!hoveredFeature) return;
      map.setFeatureState(hoveredFeature, { hover: false });
      hoveredFeature = undefined;
    };
    const handleMouseMove = (event) => {
      if (map.getLayer(stillWaterCellsFillLayerId)) {
        const cellFeatures = map.queryRenderedFeatures(event.point, {
          layers: [stillWaterCellsFillLayerId],
        });
        if (cellFeatures.length > 0) {
          map.getCanvas().style.cursor = 'pointer';
          return;
        }
      }

      const feature = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      })[0];
      if (!feature) {
        clearHoveredFeature();
        map.getCanvas().style.cursor = '';
        return;
      }

      const featureStateTarget = {
        source: feature.source,
        sourceLayer: feature.sourceLayer,
        id: feature.id,
      };
      if (
        featureStateTarget.id !== undefined
        && featureStateTarget.id !== null
        && (
          !hoveredFeature
          || hoveredFeature.id !== featureStateTarget.id
          || hoveredFeature.source !== featureStateTarget.source
        )
      ) {
        clearHoveredFeature();
        hoveredFeature = featureStateTarget;
        map.setFeatureState(hoveredFeature, { hover: true });
      }
      map.getCanvas().style.cursor = 'pointer';
    };
    map.on('mousemove', handleMouseMove);

    return () => {
      clickHandlers.forEach(({ layerId, handler }) => map.off('click', layerId, handler));
      map.off('mousemove', handleMouseMove);
      clearHoveredFeature();
      map.getCanvas().style.cursor = '';
      if (map.getLayer(layers.stillWater.id)) {
        map.setFilter(layers.stillWater.id, null);
      }
      if (map.getLayer(layers.stillWaterOutline.id)) {
        map.setFilter(layers.stillWaterOutline.id, null);
      }
      [...layerEntries].reverse().forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      [...sourceEntries].reverse().forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [map, navigate, stilstaandWaterId]);

  return (
    <div className="still-water">
      <PanelHeader
        map={map}
        rootPath="/bronnen"
        parentPath={`/bronnen/stilstaand-water/${stilstaandWaterId}`}
        parentName={name}
        currentName={`Cel ${cellId}`}
      />
      <PanelList details={details} />
    </div>
  );
}

export default StillWaterCell;
